import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/country-select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { myBillingQuery } from "@/lib/queries";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/app/billing")({
  head: () =>
    privatePageMetadata(
      "/app/billing",
      "Billing | BeHot.Now",
      "Manage your billing details and invoices.",
    ),
  component: BillingPage,
});

const FIELDS = [
  ["company_name", "dashboard.billing.field.companyName"],
  ["tax_id", "dashboard.billing.field.taxId"],
  ["billing_email", "dashboard.billing.field.billingEmail"],
  ["address_line1", "dashboard.billing.field.addressLine1"],
  ["address_line2", "dashboard.billing.field.addressLine2"],
  ["city", "dashboard.billing.field.city"],
  ["postal_code", "dashboard.billing.field.postalCode"],
  ["country_code", "dashboard.billing.field.country"],
] as const;

type FieldKey = (typeof FIELDS)[number][0];

function BillingPage() {
  const { user } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data: billing } = useQuery(myBillingQuery(user?.id));
  const [values, setValues] = useState<Record<FieldKey, string>>({
    company_name: "",
    tax_id: "",
    billing_email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postal_code: "",
    country_code: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!billing) return;
    setValues((prev) => {
      const next = { ...prev };
      for (const [key] of FIELDS) {
        next[key] = (billing as Record<string, string | null>)[key] ?? "";
      }
      return next;
    });
  }, [billing]);

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("billing_profiles").upsert({
        user_id: user.id,
        company_name: values.company_name.trim() || null,
        tax_id: values.tax_id.trim() || null,
        billing_email: values.billing_email.trim() || null,
        address_line1: values.address_line1.trim() || null,
        address_line2: values.address_line2.trim() || null,
        city: values.city.trim() || null,
        postal_code: values.postal_code.trim() || null,
        country_code: values.country_code || null,
      });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["my-billing", user.id] });
      toast.success(t("dashboard.billing.toast.saved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("dashboard.billing.toast.error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="card-surface flex items-start gap-3 p-5">
        <CreditCard className="mt-0.5 size-5 text-primary" />
        <div>
          <p className="font-semibold">{t("dashboard.billing.paymentsHow")}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("dashboard.billing.paymentsDesc")}
          </p>
        </div>
      </div>

      <form onSubmit={save} className="card-surface space-y-4 p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, labelKey]) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={key}>{t(labelKey)}</Label>
              {key === "country_code" ? (
                <CountrySelect
                  id={key}
                  value={values[key]}
                  onChange={(v) => setValues((prev) => ({ ...prev, country_code: v }))}
                />
              ) : (
                <Input
                  id={key}
                  value={values[key]}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  {...(key === "billing_email" ? { type: "email" } : {})}
                />
              )}
            </div>
          ))}
        </div>
        <Button type="submit" variant="hot" disabled={saving}>
          {saving ? t("dashboard.billing.saving") : t("dashboard.billing.save")}
        </Button>
      </form>
    </div>
  );
}
